import { defaultRequestConfig, } from '../core';
import { getJson, stringify, parseDataByType } from '../helpers/utils';
import { CancelRequestToken } from './cancel';
import { mergeConfigs } from '../helpers/mergeConfigs';
/**
 * Client implementation of http library
 */
export class HttpClient {
    constructor(config = defaultRequestConfig) {
        this.isCancel = (error) => {
            return error.name === 'AbortError';
        };
        this.defaultConfig = { ...defaultRequestConfig, ...config };
        this.core = typeof window !== 'undefined' ? window.fetch.bind(window) : fetch;
    }
    abortController() {
        return new CancelRequestToken();
    }
    /**
     * Multipurpose method for making http requests
     * @param {string} url
     * @param {HttpRequestConfig} config
     */
    request(url, config) {
        const fullConfig = mergeConfigs(this.defaultConfig, config);
        const { baseURL = '', params = {} } = fullConfig;
        const _url = baseURL + url + stringify(params);
        const clientConfig = this.convertServerConfigToClient(fullConfig);
        return this.core(_url, clientConfig)
            .then((response) => this.convertClientResponse(url, response, fullConfig))
            .catch((error) => this.throwError(error, fullConfig));
    }
    get(url, config) {
        return this.request(url, { ...config, method: 'GET' });
    }
    delete(url, config) {
        return this.request(url, { ...config, method: 'DELETE' });
    }
    post(url, data, config) {
        return this.request(url, { ...config, data, method: 'POST' });
    }
    put(url, data, config) {
        return this.request(url, { ...config, data, method: 'PUT' });
    }
    // put: (url: string, data: unknown, config?: HttpRequestConfig) => Promise<unknown>;
    patch(url, data, config) {
        return this.request(url, { ...config, data, method: 'PATCH' });
    }
    /**
     * Preparing unified configuration to using with native `fetch`
     * @param config
     */
    convertServerConfigToClient(config) {
        const contentType = (config.headers && config.headers['Content-Type']) || '';
        const needJSON = contentType.indexOf('application/json') !== -1;
        const clientConfig = {
            method: config.method,
            body: needJSON ? getJson(config.data) : config.data,
            headers: config.headers,
            signal: config.cancelToken,
            credentials: config.withCredentials ? 'include' : 'same-origin',
        };
        return clientConfig;
    }
    /**
     * Converting window.fetch response to unified interface of this library
     * @param url
     * @param response
     * @param config
     */
    async convertClientResponse(url, response, config) {
        let result;
        if (response instanceof Error) {
            this.throwError(response, config);
        }
        else {
            const data = await parseDataByType(response);
            const outResponse = {
                data,
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
                config: config,
                request: null,
            };
            result = outResponse;
        }
        return result;
    }
    throwError(error, config) {
        const common = {
            config,
            request: undefined,
        };
        let result;
        if (typeof error === 'object' && 'response' in error) {
            const { response } = error;
            result = {
                name: '',
                stack: '',
                message: '',
                response,
                ...common,
            };
        }
        else {
            const { message, stack, name } = error;
            result = {
                message,
                stack,
                name,
                ...common,
            };
        }
        throw result;
    }
}
